import { ErrorMessage } from "client/Features/AddressBook/ErrorMessage";
import { EMPTY_ARRAY, generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { ItemView } from "./ItemView";
import {
	ISearchResultItem,
	SearchCategoryResult,
	SearchCategoryResultStatus
} from "./MultiScopeSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	SearchIntegrationKind,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { Anchor } from "client/Components";
import { ItemLayout } from "../../Items/ItemLayout";
import { useCallbackWithoutArguments, useKeyboardListNavigationItem } from "client/Hooks";
import { SearchListItem } from "../../SearchListItem";
import { ScopeSection } from "../../ScopeSection";
import { ContactSource } from "client/Api/Email/contacts";
import { Key } from "client/Misc/commonTypes";

export type CategoryViewProps = {
	result: SearchCategoryResult;
	isMobile: boolean;
	navigationStartIndex: number;
	timezone: TimeZone;
	onViewMore: (scope: GlobalSearchScopeIdentifier) => void;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
};

export const CategoryView = (props: CategoryViewProps): ReactElement => {
	const {
		result,
		isMobile,
		navigationStartIndex,
		timezone,
		onViewMore,
		onOpenContactDetails
	} = props;

	const {
		itemsScope,
		name,
		status,
		items,
		hasMore
	} = result;

	const scope: SearchIntegrationKind | string = itemsScope.kind === GlobalSearchScopeKind.Predefined
		? itemsScope.predefinedKind
		: `integration-id-${itemsScope.integrationId}`;

	const itemsToRender: readonly ISearchResultItem[] = items ?? EMPTY_ARRAY;

	return (
		<ScopeSection
			trackingId={generateTrackingId("category", `|${scope}`)}
			title={name}
		>
			{status === SearchCategoryResultStatus.Failed && (
				<ErrorMessage message={localize("_GlobalSearchError")} />
			)}

			{(itemsToRender).map(
				(item, index) => {
					const navigationIndex: number = navigationStartIndex + index;

					return (
						<ItemView
							key={index}
							item={item}
							index={index}
							navigationIndex={navigationIndex}
							isMobile={isMobile}
							itemsScope={itemsScope}
							timezone={timezone}
							onOpenContactDetails={onOpenContactDetails}
						/>
					);
				})
			}

			{hasMore && (
				<ViewMoreItems
					onInvoke={() => onViewMore(itemsScope)}
					navigationIndex={navigationStartIndex + itemsToRender.length}
				/>
			)}
		</ScopeSection>
	);
};
CategoryView.displayName = "CategoryView";

type ViewMoreItemsProps = {
	onInvoke: () => void;
	navigationIndex: number;
};

const ViewMoreItems = (props: ViewMoreItemsProps): ReactElement => {
	const {
		onInvoke: onInvokeFromProps,
		navigationIndex
	} = props;

	const onInvoke: () => void = useCallbackWithoutArguments(onInvokeFromProps);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onInvoke,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<SearchListItem
			onExecute={onInvoke}
			trackingId="view-more"
		>
			<ItemLayout
				hasNoHover
				ref={ref}
				onKeyDown={onKeyDown}
				tabIndex={-1}
			>
				<ItemLayout.InfoWrapper>
					<ItemLayout.Title
						title={localize("_ViewMore")}
					>
						<Anchor
							onClick={onInvoke}
							title={localize("_ViewMore")}
							tabIndex={-1}
						>
							{localize("_ViewMore")}
						</Anchor>
					</ItemLayout.Title>
				</ItemLayout.InfoWrapper>
			</ItemLayout>
		</SearchListItem>
	);
};
ViewMoreItems.displayName = "ViewMoreItems";
